import { useAuth } from "context/AuthContext";
import { replyMuteLabel } from "utils/replyMuteLabel";
import {
  Check,
  ChevronDown,
  Copy,
  Mail,
  MoreVertical,
  Phone,
  TriangleAlert,
} from "lucide-react";
import { useRef, useState } from "react";
import fetchWithCredentials from "apiClient";
import {
  AutoResponseRule,
  DEFAULT_AUTO_RESPONSE_RULE,
} from "components/CondoManagerWhatsappCard";
import { DisturbScheduleCard } from "components/DisturbScheduleCard";
import { Button } from "./ui/Button";
import { Dialog } from "./ui/Dialog";
import { Modal } from "./ui/Modal";
import { WhatsAppIcon } from "./WhatsAppIcon";

type ContactDetailKind = "whatsapp" | "phone" | "mail";

function normalizeDisturbRules(raw: unknown): AutoResponseRule[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((rule) => {
    const item = (rule || {}) as AutoResponseRule;
    return {
      enabled: item.enabled !== false,
      weekdays: Array.isArray(item.weekdays)
        ? item.weekdays.filter((d) => Number.isInteger(d))
        : [],
      start: typeof item.start === "string" ? item.start : "09:00",
      end: typeof item.end === "string" ? item.end : "18:00",
    };
  });
}

interface ContactDetailRowProps {
  kind: ContactDetailKind;
  label: string;
  value: string;
  href: string;
  copyLabel: string;
  copiedLabel: string;
}

function ContactDetailRow({
  kind,
  label,
  value,
  href,
  copyLabel,
  copiedLabel,
}: ContactDetailRowProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy contact detail:", err);
    }
  };

  return (
    <div className="flex items-center gap-2.5 rounded-lg py-1.5 pr-1 hover:bg-stone-50/80">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          kind === "whatsapp" ? "bg-accent/10 text-accent" : "bg-stone-100 text-gray-600"
        }`}
        aria-hidden
      >
        {kind === "whatsapp" ? (
          <WhatsAppIcon className="h-4 w-4" />
        ) : kind === "mail" ? (
          <Mail className="h-4 w-4" />
        ) : (
          <Phone className="h-4 w-4" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <span className="block text-[10px] font-medium uppercase tracking-wide text-gray-400">
          {label}
        </span>
        <a
          href={href}
          target={kind === "mail" ? undefined : "_blank"}
          rel={kind === "mail" ? undefined : "noopener noreferrer"}
          className="block truncate text-sm text-primary hover:underline"
        >
          {value}
        </a>
      </div>
      <button
        type="button"
        onClick={() => void handleCopy()}
        className="inline-flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-400 transition-colors hover:bg-stone-100 hover:text-gray-700"
        aria-label={copied ? copiedLabel : copyLabel}
        title={copied ? copiedLabel : copyLabel}
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-600" aria-hidden />
        ) : (
          <Copy className="h-4 w-4" aria-hidden />
        )}
      </button>
    </div>
  );
}

interface ContactSummaryProps {
  name: string;
  phone: string;
  landlinePhone: string;
  mail: string;
  wappPhoneInvalid?: boolean;
  condoName?: string;
  roleLabel?: string;
  /** Extra lines shown under the name (unit, role, service type, etc.). */
  contextLines?: string[];
  residentId?: number;
  boardMemberId?: number;
  isResidentActive?: boolean;
  workerId?: number;
  isWorkerActive?: boolean;
  isWorkerOnCurrentCondo?: boolean;
  providerId?: number;
  pendingContactId?: number;
  isProviderOnCurrentCondo?: boolean;
  condoId?: number;
  actionLoading?: boolean;
  onDeactivate?: () => Promise<void>;
  onActivate?: () => Promise<void>;
  onRemoveFromBoard?: () => Promise<void>;
  onAddWorkerToCondo?: () => Promise<void>;
  onAddProviderToCondo?: () => Promise<void>;
  onRemoveProviderFromCondo?: () => Promise<void>;
  onAcceptPendingContact?: () => Promise<void>;
  onRejectPendingContact?: () => Promise<void>;
  showAutoResponseMenuAction?: boolean;
  showAutoResponseToggleStrip?: boolean;
  autoResponseEnabled?: boolean;
  autoResponseLoading?: boolean;
  onToggleAutoResponse?: (enabled: boolean) => Promise<void>;
  /** "self" = manager's global WhatsApp auto-response; "contact" = per-contact setting */
  autoResponseVariant?: "self" | "contact";
  workerDisturbSchedule?: AutoResponseRule[] | null;
  onWorkerDisturbScheduleSaved?: () => Promise<void> | void;
  replyMute?: import("types/ReplyMute").ReplyMute | null;
  resumeRepliesLoading?: boolean;
  onResumeReplies?: () => Promise<void>;
}

function normalizeTel(phone: string): string {
  return phone.replace(/\s+/g, "");
}

function whatsAppUrl(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : "#";
}

export function ContactSummary({
  name,
  phone,
  landlinePhone,
  mail,
  wappPhoneInvalid = false,
  condoName,
  roleLabel,
  contextLines = [],
  residentId,
  boardMemberId,
  isResidentActive,
  workerId,
  isWorkerActive,
  isWorkerOnCurrentCondo,
  providerId,
  pendingContactId,
  isProviderOnCurrentCondo,
  condoId,
  actionLoading,
  onDeactivate,
  onActivate,
  onRemoveFromBoard,
  onAddWorkerToCondo,
  onAddProviderToCondo,
  onRemoveProviderFromCondo,
  onAcceptPendingContact,
  onRejectPendingContact,
  showAutoResponseMenuAction = false,
  showAutoResponseToggleStrip = false,
  autoResponseEnabled = false,
  autoResponseLoading = false,
  onToggleAutoResponse,
  autoResponseVariant = "self",
  workerDisturbSchedule = null,
  onWorkerDisturbScheduleSaved,
  replyMute = null,
  resumeRepliesLoading = false,
  onResumeReplies,
}: ContactSummaryProps) {
  const { lang } = useAuth();
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [disturbScheduleOpen, setDisturbScheduleOpen] = useState(false);
  const [disturbRules, setDisturbRules] = useState<AutoResponseRule[]>([]);
  const [disturbScheduleLoading, setDisturbScheduleLoading] = useState(false);
  const [disturbScheduleSaving, setDisturbScheduleSaving] = useState(false);
  const [disturbScheduleError, setDisturbScheduleError] = useState<string | null>(
    null,
  );
  const actionButtonRef = useRef<HTMLButtonElement | null>(null);
  const locale = lang === "es" ? "es" : "en";
  const tr = (es: string, en: string) => (locale === "es" ? es : en);
  const showBoardActions =
    boardMemberId != null && condoId != null && Boolean(onRemoveFromBoard);
  const showResidentActions =
    !showBoardActions &&
    residentId != null &&
    condoId != null &&
    (onDeactivate || onActivate);
  const showWorkerActions =
    workerId != null &&
    condoId != null &&
    isWorkerOnCurrentCondo !== false &&
    (onDeactivate || onActivate);
  const showWorkerAttachAction =
    workerId != null &&
    condoId != null &&
    isWorkerOnCurrentCondo === false &&
    onAddWorkerToCondo;
  const showWorkerDisturbScheduleAction =
    workerId != null && condoId != null && isWorkerOnCurrentCondo !== false;
  const showProviderActions =
    providerId != null &&
    condoId != null &&
    (onAddProviderToCondo || onRemoveProviderFromCondo);

  const addProviderLabel = locale === "es" ? "Añadir al edificio" : "Add to building";
  const addWorkerLabel = locale === "es" ? "Añadir al edificio" : "Add to building";
  const removeProviderLabel =
    locale === "es" ? "Quitar del edificio" : "Remove from building";
  const removeFromBoardLabel =
    locale === "es" ? "Quitar de la comisión" : "Remove from board";
  const deactivateLabel = locale === "es" ? "Desactivar" : "Deactivate";
  const reactivateLabel = locale === "es" ? "Reactivar" : "Reactivate";
  const autoResponseOnLabel =
    autoResponseVariant === "contact"
      ? locale === "es"
        ? "Activar respuesta automática para este contacto"
        : "Turn auto-response ON for this contact"
      : locale === "es"
        ? "Activar respuesta automática"
        : "Turn auto-response ON";
  const autoResponseOffLabel =
    autoResponseVariant === "contact"
      ? locale === "es"
        ? "Desactivar respuesta automática para este contacto"
        : "Turn auto-response OFF for this contact"
      : locale === "es"
        ? "Desactivar respuesta automática"
        : "Turn auto-response OFF";
  const acceptLabel = locale === "es" ? "Aceptar" : "Accept";
  const rejectLabel = locale === "es" ? "Rechazar" : "Reject";

  const canShowMenu =
    showBoardActions ||
    showResidentActions ||
    showWorkerActions ||
    showWorkerAttachAction ||
    showWorkerDisturbScheduleAction ||
    showProviderActions ||
    Boolean(showAutoResponseMenuAction && onToggleAutoResponse) ||
    Boolean(
      pendingContactId &&
        condoId &&
        (onAcceptPendingContact || onRejectPendingContact)
    );

  const hasContactDetails = Boolean(phone || landlinePhone || mail);
  const copyLabel = locale === "es" ? "Copiar" : "Copy";
  const copiedLabel = locale === "es" ? "Copiado" : "Copied";
  const whatsAppLabel = "WhatsApp";
  const landlineLabel = locale === "es" ? "Teléfono fijo" : "Landline";
  const mailLabel = locale === "es" ? "Correo" : "Email";
  const invalidWhatsAppMessage =
    locale === "es"
      ? "Este número no es un WhatsApp válido. Actualizá el teléfono del contacto para poder enviar mensajes."
      : "This number is not a valid WhatsApp number. Update the contact's phone number to send messages.";

  const openWorkerDisturbSchedule = async () => {
    if (workerId == null || condoId == null) return;
    setIsActionsOpen(false);
    setDisturbScheduleOpen(true);
    setDisturbScheduleError(null);
    setDisturbRules(normalizeDisturbRules(workerDisturbSchedule));
    setDisturbScheduleLoading(true);
    try {
      const data = await fetchWithCredentials(
        `/manager-api/workers/${workerId}/disturb-schedule/?condo=${condoId}`,
      );
      setDisturbRules(normalizeDisturbRules(data?.disturbSchedule));
    } catch (err) {
      console.error("Error loading worker disturb schedule:", err);
      setDisturbScheduleError(
        tr(
          "No se pudo cargar el horario disponible.",
          "Could not load available hours.",
        ),
      );
    } finally {
      setDisturbScheduleLoading(false);
    }
  };

  const saveWorkerDisturbSchedule = async () => {
    if (workerId == null || condoId == null) return;
    setDisturbScheduleSaving(true);
    setDisturbScheduleError(null);
    try {
      const parsedRules = disturbRules.map((rule) => ({
        enabled: rule.enabled !== false,
        weekdays: Array.isArray(rule.weekdays)
          ? Array.from(new Set(rule.weekdays)).sort((a, b) => a - b)
          : [],
        start: rule.start || "09:00",
        end: rule.end || "18:00",
      }));
      await fetchWithCredentials(
        `/manager-api/workers/${workerId}/disturb-schedule/?condo=${condoId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ disturb_schedule: parsedRules }),
        },
      );
      await onWorkerDisturbScheduleSaved?.();
      setDisturbScheduleOpen(false);
    } catch (err) {
      console.error("Error saving worker disturb schedule:", err);
      setDisturbScheduleError(
        tr(
          "No se pudo guardar el horario disponible.",
          "Could not save available hours.",
        ),
      );
    } finally {
      setDisturbScheduleSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-stone-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 p-3 lg:p-4">
        <div className="min-w-0 flex-1">
          <div className="sr-only lg:not-sr-only flex flex-wrap items-center gap-x-2 gap-y-1">
            <h2 className="min-w-0 truncate text-lg font-semibold text-gray-900">
              {name}
            </h2>
            {wappPhoneInvalid && phone && (
              <span
                className="inline-flex shrink-0 text-amber-600"
                title={invalidWhatsAppMessage}
                aria-label={invalidWhatsAppMessage}
              >
                <TriangleAlert className="h-4 w-4" aria-hidden />
              </span>
            )}
            {roleLabel && (
              <span className="inline-flex shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-600">
                {roleLabel}
              </span>
            )}
            {condoName && (
              <span className="inline-flex max-w-full shrink-0 truncate rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {condoName}
              </span>
            )}
          </div>
          {contextLines.length > 0 && (
            <div className="mt-1 space-y-0.5 lg:mt-1.5">
              {contextLines.map((line, index) => (
                <p
                  key={`${index}-${line}`}
                  className="text-sm leading-snug text-gray-600"
                >
                  {line}
                </p>
              ))}
            </div>
          )}
        </div>
        {canShowMenu && (
          <>
          <button
            ref={actionButtonRef}
            type="button"
            onClick={() => setIsActionsOpen((prev) => !prev)}
            disabled={actionLoading || autoResponseLoading}
            className="inline-flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-600 transition-colors hover:bg-stone-100 disabled:opacity-60"
            aria-label={locale === "es" ? "Acciones del contacto" : "Contact actions"}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        <Dialog
          open={isActionsOpen}
          triggerRef={actionButtonRef}
          onClose={() => setIsActionsOpen(false)}
          position="bottom-start"
          className="w-56"
        >
          <div className="space-y-1 p-0">
            {showBoardActions && onRemoveFromBoard && (
              <button
                type="button"
                onClick={async () => {
                  await onRemoveFromBoard();
                  setIsActionsOpen(false);
                }}
                disabled={actionLoading}
                className="w-full rounded px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                {removeFromBoardLabel}
              </button>
            )}
            {showResidentActions &&
              (isResidentActive ? (
                onDeactivate && (
                  <button
                    type="button"
                    onClick={async () => {
                      await onDeactivate();
                      setIsActionsOpen(false);
                    }}
                    disabled={actionLoading}
                    className="w-full rounded px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                  >
                    {deactivateLabel}
                  </button>
                )
              ) : (
                onActivate && (
                  <button
                    type="button"
                    onClick={async () => {
                      await onActivate();
                      setIsActionsOpen(false);
                    }}
                    disabled={actionLoading}
                    className="w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-60"
                  >
                    {reactivateLabel}
                  </button>
                )
              ))}
            {showWorkerActions &&
              (isWorkerActive ? (
                onDeactivate && (
                  <button
                    type="button"
                    onClick={async () => {
                      await onDeactivate();
                      setIsActionsOpen(false);
                    }}
                    disabled={actionLoading}
                    className="w-full rounded px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                  >
                    {deactivateLabel}
                  </button>
                )
              ) : (
                onActivate && (
                  <button
                    type="button"
                    onClick={async () => {
                      await onActivate();
                      setIsActionsOpen(false);
                    }}
                    disabled={actionLoading}
                    className="w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-60"
                  >
                    {reactivateLabel}
                  </button>
                )
              ))}
            {showWorkerAttachAction && (
              <button
                type="button"
                onClick={async () => {
                  await onAddWorkerToCondo?.();
                  setIsActionsOpen(false);
                }}
                disabled={actionLoading}
                className="w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-60"
              >
                {addWorkerLabel}
              </button>
            )}
            {showWorkerDisturbScheduleAction && (
              <button
                type="button"
                onClick={() => void openWorkerDisturbSchedule()}
                disabled={actionLoading || disturbScheduleLoading}
                className="w-full rounded px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {tr("Horario disponible", "Available hours")}
              </button>
            )}
            {showProviderActions &&
              (isProviderOnCurrentCondo ? (
                onRemoveProviderFromCondo && (
                  <button
                    type="button"
                    onClick={async () => {
                      await onRemoveProviderFromCondo();
                      setIsActionsOpen(false);
                    }}
                    disabled={actionLoading}
                    className="w-full rounded px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                  >
                    {removeProviderLabel}
                  </button>
                )
              ) : (
                onAddProviderToCondo && (
                  <button
                    type="button"
                    onClick={async () => {
                      await onAddProviderToCondo();
                      setIsActionsOpen(false);
                    }}
                    disabled={actionLoading}
                    className="w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-60"
                  >
                    {addProviderLabel}
                  </button>
                )
              ))}
            {showAutoResponseMenuAction && onToggleAutoResponse && (
              <button
                type="button"
                onClick={async () => {
                  await onToggleAutoResponse(!autoResponseEnabled);
                  setIsActionsOpen(false);
                }}
                disabled={autoResponseLoading}
                className="w-full rounded px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {autoResponseEnabled ? autoResponseOffLabel : autoResponseOnLabel}
              </button>
            )}
            {pendingContactId != null &&
              condoId != null &&
              onAcceptPendingContact && (
                <button
                  type="button"
                  onClick={async () => {
                    await onAcceptPendingContact();
                    setIsActionsOpen(false);
                  }}
                  disabled={actionLoading}
                  className="w-full rounded px-3 py-2 text-left text-sm hover:bg-green-50 text-green-700 disabled:opacity-60"
                >
                  {acceptLabel}
                </button>
              )}
            {pendingContactId != null &&
              condoId != null &&
              onRejectPendingContact && (
                <button
                  type="button"
                  onClick={async () => {
                    await onRejectPendingContact();
                    setIsActionsOpen(false);
                  }}
                  disabled={actionLoading}
                  className="w-full rounded px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                >
                  {rejectLabel}
                </button>
              )}
          </div>
        </Dialog>
          </>
        )}
      </div>

      <Modal
        isOpen={disturbScheduleOpen}
        onClose={() => {
          if (!disturbScheduleSaving) setDisturbScheduleOpen(false);
        }}
        title={tr("Horario disponible", "Available hours")}
      >
        <div className="space-y-4">
          {disturbScheduleLoading ? (
            <p className="text-sm text-gray-500">
              {tr("Cargando…", "Loading…")}
            </p>
          ) : (
            <DisturbScheduleCard
              tr={tr}
              locale={locale}
              showHeader={false}
              title={tr("Horario disponible", "Available hours")}
              description={tr(
                "Fuera de estos horarios, Eloria no le enviará mensajes salvo que sean urgentes.",
                "Outside these hours, Eloria will not message them unless it is urgent.",
              )}
              rules={disturbRules}
              onAddRule={() =>
                setDisturbRules((prev) => [
                  ...prev,
                  { ...DEFAULT_AUTO_RESPONSE_RULE },
                ])
              }
              onRemoveRule={(index) =>
                setDisturbRules((prev) => prev.filter((_, i) => i !== index))
              }
              onUpdateRule={(index, patch) =>
                setDisturbRules((prev) =>
                  prev.map((rule, i) =>
                    i === index ? { ...rule, ...patch } : rule,
                  ),
                )
              }
              onToggleRuleWeekday={(index, weekday) =>
                setDisturbRules((prev) =>
                  prev.map((rule, i) => {
                    if (i !== index) return rule;
                    const weekdays = Array.isArray(rule.weekdays)
                      ? rule.weekdays
                      : [];
                    const exists = weekdays.includes(weekday);
                    const nextWeekdays = exists
                      ? weekdays.filter((d) => d !== weekday)
                      : [...weekdays, weekday];
                    return {
                      ...rule,
                      weekdays: nextWeekdays.sort((a, b) => a - b),
                    };
                  }),
                )
              }
            />
          )}
          {disturbScheduleError ? (
            <p className="text-sm text-red-600">{disturbScheduleError}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setDisturbScheduleOpen(false)}
              disabled={disturbScheduleSaving}
            >
              {tr("Cancelar", "Cancel")}
            </Button>
            <Button
              onClick={() => void saveWorkerDisturbSchedule()}
              disabled={disturbScheduleLoading || disturbScheduleSaving}
            >
              {disturbScheduleSaving
                ? tr("Guardando…", "Saving…")
                : tr("Guardar", "Save")}
            </Button>
          </div>
        </div>
      </Modal>

      {replyMute && onResumeReplies && (
        <div className="flex items-center justify-between gap-3 border-t border-amber-100 bg-amber-50 px-3 py-2 lg:px-4">
          <p className="min-w-0 text-xs font-medium text-amber-900">
            {replyMuteLabel(replyMute, locale)}
          </p>
          <button
            type="button"
            onClick={() => void onResumeReplies()}
            disabled={resumeRepliesLoading}
            className="shrink-0 rounded-md bg-amber-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-800 disabled:opacity-60"
          >
            {resumeRepliesLoading
              ? tr("Reactivando…", "Turning back on…")
              : tr("Reactivar Eloria", "Turn Eloria back on")}
          </button>
        </div>
      )}

      {showAutoResponseToggleStrip && onToggleAutoResponse && (
        <div className="flex items-center justify-between gap-3 border-t border-stone-100 px-3 py-2 lg:px-4">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-700">
              {locale === "es" ? "Respuesta automática" : "Automatic replies"}
            </p>
            <p className="text-[10px] text-gray-500">
              {autoResponseEnabled
                ? locale === "es"
                  ? "Eloria responde por WhatsApp en tu nombre"
                  : "Eloria replies on WhatsApp on your behalf"
                : locale === "es"
                  ? "Desactivada: no se envían respuestas automáticas"
                  : "Off: no automatic replies are sent"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onToggleAutoResponse(!autoResponseEnabled)}
            disabled={autoResponseLoading}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              autoResponseEnabled ? "bg-green-500" : "bg-gray-300"
            } ${autoResponseLoading ? "cursor-not-allowed opacity-60" : ""}`}
            aria-pressed={autoResponseEnabled}
            aria-label={
              locale === "es"
                ? "Alternar respuesta automática"
                : "Toggle automatic replies"
            }
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                autoResponseEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      )}

      {hasContactDetails && (
        <div className="border-t border-stone-100">
          <button
            type="button"
            onClick={() => setDetailsOpen((o) => !o)}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-gray-600 hover:bg-stone-50 lg:px-4"
            aria-expanded={detailsOpen}
          >
            {locale === "es" ? "Datos de contacto" : "Contact details"}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${detailsOpen ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>
          {detailsOpen && (
            <div className="space-y-0.5 border-t border-stone-50 px-3 pb-3 lg:px-4">
              {phone && (
                <ContactDetailRow
                  kind="whatsapp"
                  label={whatsAppLabel}
                  value={phone}
                  href={whatsAppUrl(phone)}
                  copyLabel={`${copyLabel} ${whatsAppLabel}`}
                  copiedLabel={copiedLabel}
                />
              )}
              {wappPhoneInvalid && phone && (
                <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-2 py-2 text-xs text-amber-900">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{invalidWhatsAppMessage}</span>
                </p>
              )}
              {landlinePhone && (
                <ContactDetailRow
                  kind="phone"
                  label={landlineLabel}
                  value={landlinePhone}
                  href={`tel:${normalizeTel(landlinePhone)}`}
                  copyLabel={`${copyLabel} ${landlineLabel}`}
                  copiedLabel={copiedLabel}
                />
              )}
              {mail && (
                <ContactDetailRow
                  kind="mail"
                  label={mailLabel}
                  value={mail}
                  href={`mailto:${mail}`}
                  copyLabel={`${copyLabel} ${mailLabel}`}
                  copiedLabel={copiedLabel}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
